import React, { Component, Fragment } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { inject } from 'mobx-react';
import { Progress } from 'reactstrap';
import cn from 'classnames';
import saveAs from 'file-saver';
import Uppy from '@uppy/core';
import { DragDrop } from '@uppy/react';
import XHRUpload from '@uppy/xhr-upload';
import Humanize from 'humanize-plus';
import Utils from '../shared/util.js';
import FileService from '../../shared/file.service.js';
import '@uppy/drag-drop/dist/style.css';
import './drop-upload-files.scss';

class DropUploadFiles extends Component {
	state = {
		files: [],
	};

	constructor(props) {
		super(props);

		this.dragDropRef = React.createRef();
	}

	componentDidMount() {
		const {
			endpoint, authService, userStore, handleUploadFileComplete, showValidationMessage,
		} = this.props;

		this.uppy
			.use(XHRUpload, {
				endpoint,
				fieldName: 'file',
				headers: {
					Authorization: `Bearer ${authService.getAccessToken()}`,
					'X-Group': userStore.profile.id,
				},
			})
			.on('file-added', (file) => {
				const { language } = this.props;

				this.setState(prevState => ({
					files: [
						...prevState.files,
						{
							id: file.id,
							name: file.name,
							size: file.size,
							type: file.extension,
							progress: file.progress.percentage,
							...(language ? { lang: language } : {}),
						},
					],
				}));
			})
			.on('upload-progress', (file) => {
				const uppyFile = this.uppy.getFile(file.id);

				if (uppyFile) {
					const filesList = this.state.files;
					const uploadedFile = filesList.filter(f => f.id === file.id);
					uploadedFile[0].progress = uppyFile.progress.percentage;

					this.setState({
						files: filesList,
					});
				}
			})
			.on('complete', (result) => {
				const filesList = this.state.files;

				result.successful.map((successfulFile) => {
					handleUploadFileComplete({
						id: successfulFile.response.body.id,
					});

					const uploadedFile = filesList.filter(f => f.id === successfulFile.id);
					uploadedFile[0].id = successfulFile.response.body.id;
					uploadedFile[0].lang = successfulFile.response.body.lang;
					uploadedFile[0].url = successfulFile.response.body.url;

					return successfulFile;
				});

				this.setState({
					files: filesList,
				});
			})
			.on('info-visible', () => {
				const { type, message } = this.uppy.getState().info;

				showValidationMessage({ code: 'drop-upload-files', color: type, message });
			});
	}

	componentDidUpdate(prevProps) {
		const {
			endpoint, language, field, intl,
		} = this.props;

		if (language !== prevProps.language) {
			this.uppy.reset();

			if (field.value && field.value.length > 0) {
				field.value.map((file) => {
					const f = file;
					f.progress = 100;
					return f;
				});

				// eslint-disable-next-line react/no-did-update-set-state
				this.setState(
					{ files: [...field.value] },
				);
			}
		}

		this.dragDropRef.current.plugin.translator.locale.strings = {
			dropHereOr: intl.formatMessage({
				id: 'drag-drop.drag-files',
			}),
			browse: intl.formatMessage({
				id: 'drag-drop.browse',
			}),
			youCanOnlyUploadFileTypes: intl.formatMessage({
				id: 'drag-drop.invalid-file-type',
			}),
		};

		const plugin = this.dragDropRef.current.plugin;
		if (plugin.uppy.plugins && plugin.uppy.plugins.uploader && plugin.uppy.plugins.uploader.length > 0) plugin.uppy.plugins.uploader[0].opts.endpoint = endpoint;

		this.dragDropRef.current.plugin.update();
	}

	static getDerivedStateFromProps(props, state) {
		let retState = null;

		if (state.files && state.files.length === 0 && props.field.value && props.field.value.length > 0) {
			props.field.value.map((file) => {
				const f = file;
				f.progress = 100;
				return f;
			});

			retState = { files: [...props.field.value] };
		}

		return retState;
	}

	uppy = new Uppy({
		autoProceed: true,
		restrictions: {
			allowedFileTypes: this.props.validFormats,
		},
		onBeforeFileAdded: (currentFile) => {
			const { files } = this.state;
			const { language } = this.props;

			const	fileAlreadyExist = language
				? files.some(f => f.name === currentFile.name.toLowerCase() && f.lang === language)
				: files.some(f => f.name === currentFile.name.toLowerCase());

			if (fileAlreadyExist) {
				const { intl } = this.props;
				Utils.showMessageAlert('drag-drop.duplicate-file',
					toast.TYPE.ERROR, intl.formatMessage({
						id: 'drag-drop.duplicate-file',
					}));
				return false;
			}

			const fileLowerCase = { ...currentFile };
			fileLowerCase.name = fileLowerCase.name.toLowerCase();
			return fileLowerCase;
		},
	});

	fileIcons = Utils.importAllFileIcons(
		require.context('../../assets/images/file-icons', false, /\.svg$/),
	);

	removeFile = (file) => {
		let removeFileId = file.id;
		const uppyFile = this.uppy.getFile(file.id);

		if (uppyFile) {
			if (file.progress === 100 && uppyFile.response) {
				removeFileId = uppyFile.response.body.id;
			}

			this.uppy.removeFile(file.id);
		}

		const filesList = this.state.files;
		const removedFileIndex = filesList.findIndex(f => f.id === file.id);
		filesList.splice(removedFileIndex, 1);

		this.setState({
			files: filesList,
		});

		if (file.progress === 100) {
			this.props.handleRemoveFile(removeFileId);
		}
	};

	handleProgress = (file, progress) => {
		const { files } = this.state;
		const filesList = files;

		const uploadedFile = filesList.filter(f => f.id === file.id);
		uploadedFile[0].downloading = progress.loaded !== progress.total;
		uploadedFile[0].downloadProgress = Math.floor(progress.loaded * 100 / progress.total);

		this.setState({
			files: filesList,
		});
	};

	downloadFile = (file) => {
		FileService.downloadFile(file, this.handleProgress)
			.then((fileBlob) => {
				saveAs(fileBlob, file.name);
			})
			.catch((error) => {
				const { intl } = this.props;

				Utils.showErrorMessageAlert(error, toast.TYPE.ERROR, intl);
			});
	};

	render() {
		const {
			label, height, intl, disabled, language,
		} = this.props;
		const { files } = this.state;

		const fileList = language ? files.filter(f => (f.lang && f.lang === language)) : files;

		return (
			<div className="drop-upload-files">
				{label && <div className="drop-label">{label}</div>}

				{disabled && <div className="disabled-overlay" />}

				<DragDrop
					ref={this.dragDropRef}
					uppy={this.uppy}
					locale={{
						strings: {
							dropHereOr: intl.formatMessage({
								id: 'drag-drop.drag-files',
							}),
							browse: intl.formatMessage({
								id: 'drag-drop.browse',
							}),
							youCanOnlyUploadFileTypes: intl.formatMessage({
								id: 'drag-drop.invalid-file-type',
							}),
						},
					}}
					height={height}
				/>

				{fileList.map(file => (
					<div className="file-info" key={file.id}>
						<div className="file-icon">
							<img
								onClick={() => !file.downloading && this.downloadFile(file)}
								src={
									this.fileIcons[`${file.type}.svg`]
										? this.fileIcons[`${file.type}.svg`]
										: this.fileIcons['default-icon.svg']
								}
								alt={file.name}
								width="28"
								height="36"
							/>
						</div>

						<div className="file-progress">
							<div className="d-flex justify-content-between">
								<span
									className="filename"
									onClick={() => !file.downloading && this.downloadFile(file)}
								>
									{file.name}
									{' '}
									{file.downloading && (
										<FormattedMessage
											id="drag-drop.downloaded"
											values={{ percentage: file.downloadProgress }}
										/>
									)}
								</span>
								<i
									className={cn('material-icons remove-file', { 'd-none': disabled })}
									onClick={() => this.removeFile(file)}
								>
									{'cancel'}
								</i>
							</div>

							{file.progress !== 100 && (
								<Fragment>
									<Progress value={file.progress} />
									<div className="progress-text">
										<FormattedMessage
											id="drag-drop.uploading"
											values={{ percentage: file.progress }}
										/>
									</div>
								</Fragment>
							)}

							{file.progress === 100 && (
								<div className="file-size">{Humanize.fileSize(file.size)}</div>
							)}
						</div>
					</div>
				))}
			</div>
		);
	}
}

export default injectIntl(inject('authService', 'userStore')(DropUploadFiles));
